import classnames from 'classnames'
import { ReactElement, ReactNode, useEffect, useRef, useState } from 'react'

import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useIntersect from '@src/quince-site-common/src/hooks/useIntersect'
import siteStateManager from '@src/quince-site-common/src/redux/globalStateManager/SiteStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import { throttle } from '@src/utils/functions/utils'

import styles from './stickyWrapper.module.scss'

export interface StickyWrapperProps {
  renderChildren?: (isIntersecting: boolean) => ReactElement
  children?: ReactNode
  top?: string
  stickyWrapperClassName?: string
}

const StickyWrapper = (props: StickyWrapperProps) => {
  const [intersectRef, isIntersecting] = useIntersect(null, '0px', 0, true)
  const [computedTop, setComputedTop] = useState(props.top)
  const isScrollingDown = useAppSelector<boolean>(siteStateManager, siteStateManager.getIsScrollingDown)
  const isScrolledDown = useAppSelector<boolean>(siteStateManager, siteStateManager.getIsScrolledDown)
  const isSearchMode = useAppSelector<boolean>(siteStateManager, siteStateManager.getIsSearchMode)
  const { deviceType } = useDeviceInfo()
  const isDesktop = deviceType === DEVICE_TYPES.DESKTOP
  const isMobile = !isDesktop
  const [hasScrolledPast, setHasScrolledPast] = useState(false)
  const elementRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let stickyTop = props.top
    if (!stickyTop) {
      const height = document.querySelector('#site-primary-header')?.getBoundingClientRect().height ?? 0
      stickyTop = height < 200 && height >= 60 ? `${height}px` : computedTop
    }
    setComputedTop(stickyTop)
  }, [props.top, isScrollingDown, isScrolledDown, isSearchMode, computedTop])

  useEffect(() => {
    const checkScrollPosition = () => {
      if (elementRef.current) {
        const rect = elementRef.current.getBoundingClientRect()
        // Only set hasScrolledPast to true if the element is above the viewport
        setHasScrolledPast(rect.bottom < 0)
      }
    }

    checkScrollPosition() // initial call
    const throttledCheckScrollPosition = throttle(checkScrollPosition, 100)
    window.addEventListener('scroll', throttledCheckScrollPosition)

    return () => {
      window.removeEventListener('scroll', throttledCheckScrollPosition)
    }
  }, [])

  const shouldShowSticky = !isIntersecting && hasScrolledPast
  const renderChildren = () =>
    // props.renderChildren accepts negated value of whether sticky wrapper is visible
    props.renderChildren ? props.renderChildren(!shouldShowSticky) : (props.children ?? null)

  return (
    <>
      <div ref={elementRef} style={{ position: 'relative' }}>
        <div
          ref={intersectRef}
          style={{
            marginBottom: computedTop,
            width: '100%',
            position: 'absolute',
          }}
        />
      </div>
      <div
        className={getStickyClassname({
          isScrolledDown,
          isScrollingDown,
          isMobile,
          stickyWrapperClassName: props?.stickyWrapperClassName,
          shouldShowSticky,
        })}
        style={{
          top: getTopVal({ isScrolledDown, isScrollingDown, computedTop, shouldShowSticky }),
          transition: 'top 0.3s ease-in-out 0s',
        }}
      >
        {renderChildren()}
      </div>
      {shouldShowSticky && isDesktop && renderChildren()}
      {!isScrollingDown && shouldShowSticky && isMobile && (
        <div
          style={{
            position: 'static',
            height: '7.8rem',
          }}
        />
      )}
    </>
  )
}

export const getTopVal = ({
  isScrolledDown,
  isScrollingDown,
  computedTop,
  shouldShowSticky,
}: {
  isScrolledDown: boolean
  isScrollingDown: boolean
  computedTop?: string
  shouldShowSticky: boolean
}) => {
  let topVal = undefined
  if (shouldShowSticky) {
    topVal = isScrolledDown && isScrollingDown ? '0px' : computedTop
  }
  return topVal
}

export const getStickyClassname = ({
  isScrolledDown,
  isScrollingDown,
  isMobile,
  stickyWrapperClassName,
  shouldShowSticky,
}: {
  isScrolledDown: boolean
  isScrollingDown: boolean
  computedTop?: string
  isMobile: boolean
  stickyWrapperClassName?: string
  shouldShowSticky: boolean
}) => {
  // @i18n-ignore
  let stickyClassName = 'undefined'
  if (shouldShowSticky && isMobile && !isScrollingDown) {
    stickyClassName = classnames(styles.stickyWrapper, stickyWrapperClassName)
  }
  if (shouldShowSticky && !isMobile) {
    stickyClassName = classnames(
      styles.stickyWrapper,
      isScrolledDown && isScrollingDown && styles.stickyWrapper__scrollingDown,
    )
  }
  return stickyClassName
}

export default StickyWrapper
